import { ref, onMounted } from "vue";
import { f7 } from "framework7-vue";
import { Capacitor } from "@capacitor/core";
import i18n from "../i18n.js";
import { useTabletScreen } from "./useMediaQuery.js";

const STICKY_CLOSE_TIMEOUT_MS = 3000000;

export function usePwa() {
  const registration = ref(null);
  const serviceWorkerPendingUpdate = ref(false);
  let updateToast = null;
  let promptedWorker = null;
  const isTablet = useTabletScreen().matches;
  const t = i18n.global.t;

  function isEligible() {
    return (
      "serviceWorker" in window.navigator &&
      process.env.NODE_ENV === "production" &&
      !Capacitor.isNativePlatform() &&
      !["localhost", "127.0.0.1"].includes(location.hostname)
    );
  }

  async function unregisterStaleWorkers() {
    if (!("serviceWorker" in window.navigator)) return;

    try {
      const registrations =
        await window.navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations.map(async (reg) => {
          try {
            const wasUnregistered = await reg.unregister();
            if (wasUnregistered) {
              console.info("Unregistered stale service worker");
            }
          } catch (error) {
            console.warn("Could not unregister stale service worker:", error);
          }
        })
      );
    } catch (error) {
      console.warn("Could not inspect stale service workers:", error);
    }
  }

  async function initPWA() {
    if (!isEligible()) return;
    try {
      await unregisterStaleWorkers();

      const swRegistration = await window.navigator.serviceWorker.register(
        "/service-worker.js",
        { scope: "/" }
      );
      registration.value = swRegistration;

      swRegistration.addEventListener("updatefound", () => {
        const newWorker = swRegistration.installing;
        if (!newWorker) return;
        newWorker.addEventListener("statechange", () => {
          if (
            newWorker.state === "installed" &&
            window.navigator.serviceWorker.controller
          ) {
            showUpdateAvailable(newWorker);
          }
        });
      });

      // A waiting worker may survive from a previous session (e.g. a dismissed
      // update prompt). Offer the update again on the next visit.
      if (swRegistration.waiting) {
        showUpdateAvailable(swRegistration.waiting);
      }
    } catch (error) {
      console.error("Service worker registration failed:", error);
    }
  }

  function showUpdateAvailable(worker) {
    const waitingWorker = worker ?? registration.value?.waiting;
    if (!waitingWorker || promptedWorker === waitingWorker) return;
    promptedWorker = waitingWorker;
    serviceWorkerPendingUpdate.value = true;

    if (updateToast) return;

    const buttonClasses =
      "button button-small button-outline button-round button-raised";

    updateToast = f7.toast.create({
      destroyOnClose: true,
      text: `<div style="display:flex;flex-direction:column;gap:8px;align-items:center;text-align:center;">
        <span>${t("common.updateNewContent")}</span>
        <span style="display:flex;gap:8px;flex-wrap:wrap;justify-content:center;">
          <a id="pwa-update-button" class="${buttonClasses} color-primary border-color-primary" style="border-radius:32px;border-width:medium;">${t("common.update")}</a>
        </span>
      </div>`,
      horizontalPosition: "center",
      position: isTablet.value ? "top" : "bottom",
      closeButton: true,
      closeTimeout: STICKY_CLOSE_TIMEOUT_MS,
      on: {
        close: () => {
          updateToast = null;
        },
      },
    });
    updateToast.open();

    const updateButton = document.querySelector("#pwa-update-button");
    updateButton?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      updateToast.close();
      void skipWaitingAndUpdate();
    });
  }

  async function skipWaitingAndUpdate() {
    const waitingWorker = registration.value?.waiting;
    if (!waitingWorker) return;
    let reloaded = false;
    const reload = () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    };
    waitingWorker.addEventListener("statechange", () => {
      if (waitingWorker.state === "activated") reload();
    });
    waitingWorker.postMessage("SKIP_WAITING");
    // Fallback in case the waiting worker never reaches "activated"
    setTimeout(reload, 3000);
  }

  onMounted(() => {
    if (!isEligible()) return;
    initPWA();
  });

  return {
    registration,
    serviceWorkerPendingUpdate,
    initPWA,
    showUpdateAvailable,
    skipWaitingAndUpdate,
  };
}
